import { SanitizedHtmlTag } from "@components/common/sanitized-html-tag";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { ErrorWithTitle } from "@context-providers/error-boundary/error-types";
import { WarningIcon } from "@flat-ui";
import { Box, Button, Grid2, Stack, Typography } from "@mui/material";
import { removeOne } from "@store/errors/errors-slice";
import { useAppDispatch } from "@store/store-helper";
import { HEADER_HEIGHT } from "@utils/ui-utils";

interface Props {
  /** Error title with the actual error */
  error: ErrorWithTitle;

  /** Click handler for when user clicks on reload */
  onReload?(): void;

  /** Click handler for when user clicks on go to home */
  onGoHome?(): void;
}

/**
 * Displays an error message with the ability to go to home page or reload the page
 */
export function ErrorPage({ error, onReload, onGoHome }: Props): JSX.Element {
  const dispatch = useAppDispatch();

  function onReloadHandler(): void {
    dispatch(removeOne(error.id));
    onReload && onReload();
  }

  function onGoHomeHandler(): void {
    dispatch(removeOne(error.id));
    onGoHome && onGoHome();
  }

  return (
    <Box
      key={error.id}
      component="div"
      data-testid="handled-error-page"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight={`calc(100vh - ${HEADER_HEIGHT})`}
      width="100%"
      gap={2}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <WarningIcon
          sx={{
            marginRight: "10px",
          }}
        />
        <Typography variant="h6">{error.title}</Typography>
      </Stack>

      <Grid2>
        <Typography component="div">
          <b>Developer Error Message:</b>
          <SanitizedHtmlTag markup={getErrorDisplayMarkup(error.error)} />
        </Typography>
      </Grid2>
      <div>
        <Button
          onClick={onGoHomeHandler}
          variant="outlined"
          draggable={false}
          sx={{ mr: "40px" }}
        >
          Return to Home
        </Button>

        <Button onClick={onReloadHandler} variant="outlined" draggable={false}>
          Reload
        </Button>
      </div>
    </Box>
  );
}
